import { useState } from 'react';
import type { ArchiveRecord } from '../types/moderation';
import { listSubmissions } from '../lib/api';

interface ContributorCardProps {
  record: ArchiveRecord;
  onSelect: (submissionId: string) => void;
}

/**
 * Right-rail summary of the contributor for this submission, plus a
 * lazy-loaded list of their other archive entries. The "Other submissions"
 * count is only fetched after the moderator clicks Show, because each
 * query hits the list endpoint and we don't want to pay for it on every
 * detail view.
 */
export default function ContributorCard({ record, onSelect }: ContributorCardProps) {
  const { name, email, attributionPref } = record.contributor || {};
  const [expanded, setExpanded] = useState(false);
  const [others, setOthers] = useState<ArchiveRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!name && !email) return null;

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && others === null && email) {
      setLoading(true);
      setError(null);
      try {
        const res = await listSubmissions({ contributorEmail: email, pageSize: 50 });
        const filtered = res.items.filter((r) => r.submissionId !== record.submissionId);
        setOthers(filtered);
      } catch (err) {
        setError((err as Error).message || 'Failed to load contributor history');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <aside className="contributor-card" data-testid="contributor-card" aria-label="Contributor">
      <h3>Contributor</h3>
      <dl className="contributor-card__details">
        {name && (
          <>
            <dt>Name</dt>
            <dd>{name}</dd>
          </>
        )}
        {email && (
          <>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${email}`} data-testid="contributor-email">
                {email}
              </a>
            </dd>
          </>
        )}
        {attributionPref && (
          <>
            <dt>Attribution</dt>
            <dd>{attributionPref}</dd>
          </>
        )}
      </dl>
      {email && (
        <button
          type="button"
          className="btn btn-link contributor-card__history-toggle"
          onClick={handleToggle}
          data-testid="contributor-history-toggle"
        >
          {expanded
            ? 'Hide other submissions'
            : `Other submissions from this contributor${others ? ` (${others.length})` : ''}`}
        </button>
      )}
      {expanded && (
        <div className="contributor-card__history" data-testid="contributor-history">
          {loading && <div>Loading…</div>}
          {error && (
            <div className="field-error" role="alert">
              {error}
            </div>
          )}
          {others && others.length === 0 && (
            <div className="empty-state empty-state--inline">No other submissions from this contributor.</div>
          )}
          {others && others.length > 0 && (
            <>
              <ul className="contributor-card__list">
                {/* F-302: cap inline display at 5 entries; for any
                    contributor with more we surface a "View all"
                    link to the filtered submissions list. */}
                {others.slice(0, 5).map((o) => (
                  <li key={o.submissionId}>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => onSelect(o.submissionId)}
                      data-testid={`contributor-other-${o.submissionId}`}
                    >
                      {o.title || o.submissionId}
                    </button>
                    <span className="contributor-card__list-status">{o.moderationStatus}</span>
                  </li>
                ))}
              </ul>
              {others.length > 5 && (
                <a
                  href={`#/submissions?contributorEmail=${encodeURIComponent(email!)}`}
                  className="contributor-card__view-all"
                  data-testid="contributor-view-all"
                >
                  View all {others.length} submissions →
                </a>
              )}
            </>
          )}
        </div>
      )}
    </aside>
  );
}
